// ===================== Elements =====================
// The player starts Neutral. Picking an element (via level-up card) sets the
// active affinity; picking the same one again raises its level. The active
// element's onHit() runs whenever a player projectile deals damage.
import { defineElement } from '../core/registries.js';
import { CONFIG } from '../config.js';

const C = CONFIG.colors;
const lvlScale = (p) => 1 + (p.elementLevel - 1) * 0.35;

defineElement({
  id: 'neutral', name: 'Neutral', color: C.neutral, dmgFactor: 1.1,
  short: '◇', desc: 'No affinity — pure force. +10% raw damage, no side effects.',
  onHit() { /* neutral's bonus is applied at fire time via dmgFactor */ },
});

defineElement({
  id: 'fire', name: 'Fire', color: C.fire, ascendsTo: 'inferno',
  short: '🔥', desc: 'Ignites foes, dealing burn damage over time. Stacks refresh.',
  onHit(game, e, dmg, p) {
    // 0.60 (was 0.35): modelled at L8 the old burn made Fire the weakest
    // element at every job (×1.40 vs poison's ×2.8 across the board) — the one
    // dead pick in the roster. 0.60 lifts it to ~×1.7, level with Ice/Holy.
    e.burnDps = Math.max(e.burnDps, dmg * 0.60 * lvlScale(p));
    e.burnT = Math.max(e.burnT, 3);
  },
});

defineElement({
  id: 'ice', name: 'Ice', color: C.ice, ascendsTo: 'absolutezero',
  short: '❄', desc: 'Chills and slows foes — chilled targets shatter for bonus damage.',
  onHit(game, e, dmg, p) {
    // Shatter rider (checked BEFORE this hit applies its own chill): already
    // chilled targets take bonus damage, frozen-solid ones take double.
    const rider = 0.12 + (p.elementLevel - 1) * 0.06;
    if (e.freezeT > 0) game.dealDamage(e, dmg * rider * 2, { applyElement: false });
    else if (e.slowT > 0) game.dealDamage(e, dmg * rider, { applyElement: false });
    e.slowT = Math.max(e.slowT, 2);
    e.slowMult = Math.min(e.slowMult === 1 ? 0.6 : e.slowMult, 0.62 - (p.elementLevel - 1) * 0.06);
    if (!e.boss && game.rng.next() < 0.06 + (p.elementLevel - 1) * 0.02) e.freezeT = Math.max(e.freezeT, 1.0);
  },
});

defineElement({
  id: 'lightning', name: 'Lightning', color: C.lightning, ascendsTo: 'ioncascade',
  short: '⚡', desc: 'Arcs to nearby enemies, chaining bonus damage; lone foes take a weak discharge.',
  onHit(game, e, dmg, p) {
    const chains = 2 + Math.floor((p.elementLevel - 1) * 0.5);
    const near = game.enemiesInRadius(e.x, e.y, 150);
    let hit = 0;
    for (const o of near) {
      if (o === e || o.dead) continue;
      // Chain damage is a flat fraction of the triggering hit — level scales
      // the NUMBER of chains (above), never the per-bounce damage, or crowds
      // multiply the multiplier (a modelled 10.5× at L8 before this was
      // capped). 0.38 × 5 chains puts the L8 crowd factor at 2.9 — the
      // intended ceiling of the element spread.
      game.dealDamage(o, dmg * 0.38, { applyElement: false });
      game.particles_spark(o.x, o.y, C.lightning, 4);
      if (++hit >= chains) break;
    }
    // Weak grounding: with no crowd to chain through, base Lightning used to
    // add exactly nothing against a lone boss (a modelled ×1.00 — a silent
    // boss-fight trap). A tenth per unspent chain puts a solo target at ~×1.5,
    // still well under Ion Cascade's 0.17 grounding — the ascended payoff.
    const unspent = chains - hit;
    if (unspent > 0 && !e.dead) game.dealDamage(e, dmg * 0.10 * unspent, { applyElement: false });
  },
});

defineElement({
  id: 'poison', name: 'Poison', color: C.poison, ascendsTo: 'blight',
  short: '☣', desc: 'Applies stacking venom. Deadly against tanks and bosses.',
  onHit(game, e, dmg, p) {
    // Cap 4 + ⌈level/2⌉ (8 at L8) — this used to be 6 + level, the SAME 14 as
    // Blight, which made base Poison the modelled best element at every job
    // (×2.8 crowd/single/boss) and left its Ascendant nothing distinctive.
    // At 8 stacks base Poison holds ~×2.1 — still the tank-and-boss pick, no
    // longer the only right answer — and "stacks to 14" is genuinely Blight's.
    const cap = 4 + Math.ceil(p.elementLevel / 2);
    e.poisonStacks = Math.min(cap, (e.poisonStacks || 0) + 1);
    e.poisonDps = Math.max(e.poisonDps, dmg * 0.12 * lvlScale(p));
    e.poisonT = Math.max(e.poisonT, 4);
  },
});

defineElement({
  id: 'wind', name: 'Wind', color: C.wind, ascendsTo: 'maelstrom',
  short: '🌪', desc: 'Hurls foes back — the impact shears them for bonus damage; braced bosses take a windburn.',
  onHit(game, e, dmg, p) {
    const shear = 0.12 + (p.elementLevel - 1) * 0.06;
    if (e.boss) {
      // Windburn (mirrors Maelstrom at half strength): a boss can't be shoved,
      // and "no rider at all" made Wind a modelled ×1.00 against every finale.
      game.dealDamage(e, dmg * shear / 2, { applyElement: false });
      return;
    }
    const dx = e.x - p.x, dy = e.y - p.y;
    const l = Math.hypot(dx, dy) || 1;
    const push = 130 + (p.elementLevel - 1) * 40;
    e.vx += (dx / l) * push; e.vy += (dy / l) * push;
    // Gale shear: the shove itself wounds — displaced enemies take impact
    // damage scaling with level.
    game.dealDamage(e, dmg * shear, { applyElement: false });
  },
});

defineElement({
  id: 'holy', name: 'Holy', color: C.holy, ascendsTo: 'zenith',
  short: '✟', desc: 'Smites the wicked and heals you on lucky strikes.',
  onHit(game, e, dmg, p) {
    if (game.rng.next() < 0.12 + (p.elementLevel - 1) * 0.03) {
      game.dealDamage(e, dmg * 0.5 * lvlScale(p), { applyElement: false });
      game.particles_spark(e.x, e.y, C.holy, 5);
    }
    if (game.rng.next() < 0.05 && p.hp < p.maxHp) p.heal(1 + p.elementLevel);
  },
});

// ===================== Ascendant elements (tier 2) =====================
// Master an element (L8) while owning a mastered weapon (any maxed base
// weapon or any evolution — ultraweapons don't count) and the run pins the
// Ascendant card: taking it REPLACES the element with its Ascendant form
// (elementLevel stays 8). Each ascended def is an ordinary element whose
// onHit stands alone — nothing chains through the base form. Riders here use
// dealDamage + particles only; spawning projectiles from inside onHit would
// mutate the projectile pool mid-collision-iteration.

defineElement({
  id: 'inferno', name: 'Inferno', color: C.inferno, tier: 2, baseId: 'fire',
  short: '🌋',
  desc: 'ASCENDANT Fire — burns stack hotter with every hit, and strikes that fell a burning foe detonate it, searing up to 3 enemies nearby.',
  onHit(game, e, dmg, p) {
    const ls = lvlScale(p);
    const wasBurning = e.burnT > 0;
    // Stacking burn: each hit ADDS to the flame, capped per target. The cap
    // (0.95) and per-hit feed (0.16) sit above base Fire's flat 0.60 so the
    // ascension stays a real upgrade after the base-Fire rebalance — plus
    // Cinderburst below, which the flat form never gets.
    e.burnDps = Math.min(dmg * 0.95 * ls, (wasBurning ? e.burnDps : 0) + dmg * 0.16 * ls);
    e.burnT = 3;
    // Cinderburst: a direct hit that fells a burning foe detonates it.
    // (DoT-tick kills can't trigger this — _dot never runs onHit.)
    if (e.hp <= 0 && wasBurning) {
      const near = game.enemiesInRadius(e.x, e.y, 100);
      let hit = 0;
      for (const o of near) {
        if (o === e || o.dead) continue;
        game.dealDamage(o, dmg * 1.5, { applyElement: false });
        if (++hit >= 3) break;
      }
      game.particles_ring(e.x, e.y, 100, C.inferno);
    }
  },
});

defineElement({
  id: 'absolutezero', name: 'Absolute Zero', color: C.absolutezero, tier: 2, baseId: 'ice',
  short: '❆',
  desc: 'ASCENDANT Ice — chilled foes shatter for heavy bonus damage (frozen solid, double; bosses count as chilled while slowed), slows bite far deeper, and lesser foes freeze often.',
  onHit(game, e, dmg, p) {
    // Shatter rider (checked BEFORE this hit applies its own chill). Bosses
    // never freeze, but they DO slow — slowT>0 counts as chilled for them too.
    const rider = 0.24 + (p.elementLevel - 1) * 0.08;
    if (e.freezeT > 0) game.dealDamage(e, dmg * rider * 2, { applyElement: false });
    else if (e.slowT > 0) game.dealDamage(e, dmg * rider, { applyElement: false });
    e.slowT = Math.max(e.slowT, 2);
    e.slowMult = Math.min(e.slowMult, 0.36 - (p.elementLevel - 1) * 0.03);
    if (!e.boss && game.rng.next() < 0.09 + (p.elementLevel - 1) * 0.03) e.freezeT = Math.max(e.freezeT, 1.4);
  },
});

defineElement({
  id: 'ioncascade', name: 'Ion Cascade', color: C.ioncascade, tier: 2, baseId: 'lightning',
  short: '🌩',
  desc: 'ASCENDANT Lightning — arcs chain to up to 6 nearby enemies; chains that find no target discharge back into the struck foe.',
  onHit(game, e, dmg, p) {
    const chains = 3 + Math.floor((p.elementLevel - 1) * 0.5);   // 6 at L8
    const near = game.enemiesInRadius(e.x, e.y, 150);
    let hit = 0;
    for (const o of near) {
      if (o === e || o.dead) continue;
      // Flat 0.34× per bounce (down from the base 0.38×): 6 chains at L8
      // keeps the crowd factor level with the element spread's ceiling
      // instead of doubling past it.
      game.dealDamage(o, dmg * 0.34, { applyElement: false });
      game.particles_spark(o.x, o.y, C.ioncascade, 4);
      if (++hit >= chains) break;
    }
    // Grounding: unspent chains discharge into the primary target.
    const unspent = chains - hit;
    if (unspent > 0 && !e.dead) game.dealDamage(e, dmg * 0.17 * unspent, { applyElement: false });
  },
});

defineElement({
  id: 'blight', name: 'Blight', color: C.blight, tier: 2, baseId: 'poison',
  short: '🦠',
  desc: 'ASCENDANT Poison — venom stacks to 14; hits on a fully infected foe bite for bonus damage, and a felled carrier passes its whole infection to up to 3 neighbours.',
  onHit(game, e, dmg, p) {
    const cap = 6 + p.elementLevel;                              // 14 at L8
    // Necrosis: the infection is already full — the hit bites harder instead.
    if ((e.poisonStacks || 0) >= cap) game.dealDamage(e, dmg * 0.10, { applyElement: false });
    e.poisonStacks = Math.min(cap, (e.poisonStacks || 0) + 1);
    e.poisonDps = Math.max(e.poisonDps, dmg * 0.12 * lvlScale(p));
    e.poisonT = Math.max(e.poisonT, 4);
    // Contagion: a carrier felled by a direct hit passes the infection on.
    if (e.hp <= 0 && e.poisonStacks > 0) {
      const near = game.enemiesInRadius(e.x, e.y, 130);
      let hit = 0;
      for (const o of near) {
        if (o === e || o.dead) continue;
        o.poisonStacks = Math.max(o.poisonStacks || 0, e.poisonStacks);
        o.poisonDps = Math.max(o.poisonDps, e.poisonDps);
        o.poisonT = Math.max(o.poisonT, e.poisonT);
        game.particles_spark(o.x, o.y, C.blight, 3);
        if (++hit >= 3) break;
      }
    }
  },
});

defineElement({
  id: 'maelstrom', name: 'Maelstrom', color: C.maelstrom, tier: 2, baseId: 'wind',
  short: '🌀',
  desc: 'ASCENDANT Wind — hurls foes with tremendous force, shearing them and up to 2 enemies caught in the slipstream; undisplaceable bosses take a windburn instead.',
  onHit(game, e, dmg, p) {
    const shear = 0.24 + (p.elementLevel - 1) * 0.08;            // 0.80 at L8
    if (e.boss) {
      // Windburn: a boss can't be shoved, so half the shear lands as damage.
      game.dealDamage(e, dmg * shear / 2, { applyElement: false });
      return;
    }
    const dx = e.x - p.x, dy = e.y - p.y;
    const l = Math.hypot(dx, dy) || 1;
    const ux = dx / l, uy = dy / l;
    e.vx += ux * (180 + (p.elementLevel - 1) * 46);              // 500 at L8
    e.vy += uy * (180 + (p.elementLevel - 1) * 46);
    game.dealDamage(e, dmg * shear, { applyElement: false });
    // Slipstream: foes in the shove's wake are struck immediately.
    const near = game.enemiesInRadius(e.x, e.y, 110);
    let hit = 0;
    for (const o of near) {
      if (o === e || o.dead) continue;
      const ox = o.x - e.x, oy = o.y - e.y;
      const ol = Math.hypot(ox, oy) || 1;
      if ((ox * ux + oy * uy) / ol <= 0.7) continue;             // push direction only
      game.dealDamage(o, dmg * 0.35, { applyElement: false });
      game.particles_spark(o.x, o.y, C.maelstrom, 3);
      if (++hit >= 2) break;
    }
  },
});

defineElement({
  id: 'zenith', name: 'Zenith', color: C.zenith, tier: 2, baseId: 'holy',
  short: '☀',
  desc: 'ASCENDANT Holy — smites 40% of hits, each smite sunbursting into up to 2 nearby foes at reduced power, and lucky strikes heal more, more often.',
  onHit(game, e, dmg, p) {
    if (game.rng.next() < 0.12 + (p.elementLevel - 1) * 0.04) {  // 40% at L8
      const smite = dmg * 0.5 * lvlScale(p);
      game.dealDamage(e, smite, { applyElement: false });
      game.particles_spark(e.x, e.y, C.zenith, 5);
      // Sunburst: the smite radiates outward.
      const near = game.enemiesInRadius(e.x, e.y, 90);
      let hit = 0;
      for (const o of near) {
        if (o === e || o.dead) continue;
        game.dealDamage(o, smite * 0.6, { applyElement: false });
        game.particles_spark(o.x, o.y, C.zenith, 3);
        if (++hit >= 2) break;
      }
    }
    // Grace: lucky heals, doubled in chance and strength.
    if (game.rng.next() < 0.10 && p.hp < p.maxHp) p.heal(2 + p.elementLevel);
  },
});
